/* eslint-disable max-lines */
import * as React from "react";
import { useState } from "react";
import { connect } from "react-redux";

import { debounce } from "lodash";
import { Grid, Table, InputOnChangeData, Input as SemanticInput, Divider, Loader } from "semantic-ui-react";

import { Input } from "@neworbit/simpleui-input";

import { debounceDelay } from "@teamprevent/common/filtering/constants";
import { SecondaryCaseStatus, secondaryCaseStatusOptions } from "@teamprevent/common/workAdjustment/models";
import { getCaseInformation } from "@teamprevent/common/ohCase/model";
import { CaseType, PRODUCT_TYPES } from "@teamprevent/common/model";
import { useDispatch } from "@teamprevent/customer-redux";

import { useCustomMedicalTypes } from "../../../customMedicalTypes/hooks";

import { DashboardCaseType } from "../../../dashboard/model";
import { ApplicationState } from "../../../applicationState";
import { ohCasesSelector, SelectorState } from "../selectors";
import { OhCaseListModel, CASE_STATUSES } from "../model";
import { loadOhCases, loadWorkAdjustments } from "../actions";

import { Link, replace } from "redux-little-router";

const stateOptions = CASE_STATUSES.map(status => ({ key: status, value: status, text: status }));

interface OwnProps {
    include: DashboardCaseType;
}

interface StateProps {
    ohCases: OhCaseListModel[];
    path: string;
    status: string;
    secondaryStatus: string;
    caseId: string;
    employeeFirstName: string;
    employeeLastName: string;
    isDefaultCustomerType: boolean;
    caseType: string;
    allowedProducts: string[];
}

interface DispatchProps {
    onFilterCaseIdChange: (caseId: string) => void;
    onFilterFirstNameChange: (employeeFirstName: string) => void;
    onFilterLastNameChange: (employeeLastName: string) => void;
    onStatusValueChange: (status: string) => void;
    onSecondaryStatusValueChange: (secondaryStatus: string) => void;
    onFilterCaseTypeChange: (caseType: string) => void;
}

export const AllUnconnected: React.FunctionComponent<OwnProps & StateProps & DispatchProps> = (props: OwnProps & StateProps & DispatchProps) => {

    const debounceUpdateCaseIdFilter = debounce((value: string) => props.onFilterCaseIdChange(value), debounceDelay);
    const debounceUpdateFirstNameFilter = debounce((value: string) => props.onFilterFirstNameChange(value), debounceDelay);
    const debounceUpdateLastNameFilter = debounce((value: string) => props.onFilterLastNameChange(value), debounceDelay);
    const debounceUpdateCaseTypeFilter = debounce((value: string) => props.onFilterCaseTypeChange(value), debounceDelay);

    const { customMedicalTypes, isLoaded: areCustomMedicalTypesLoaded } = useCustomMedicalTypes();

    const [caseId, setCaseId] = useState(props.caseId);
    const [employeeFirstName, setEmployeeFirstName] = useState(props.employeeFirstName);
    const [employeeLastName, setEmployeeLastName] = useState(props.employeeLastName);
    const [caseType, setCaseType] = useState(props.caseType);
    const [productOptions, setProductOptions] =
        useState<{ key: CaseType; value: CaseType; text: string }[]>([]);
    const [productOptionsLoaded, setProductOptionsLoaded] = useState(false);

    const filterSeparator = ";";

    const dispatch = useDispatch();

    React.useEffect(() => {
        if (props.path === "/ohCases") {
            dispatch(loadOhCases({
                caseId: props.caseId,
                employeeFirstName: props.employeeFirstName,
                employeeLastName: props.employeeLastName,
                status: props.status,
                caseType: CaseType[props.caseType as keyof typeof CaseType],
                include: CaseType[props.include as keyof typeof CaseType],
            }));
        } else if (props.path === "/workadjustments") {
            dispatch(loadWorkAdjustments({
                status: props.status,
                caseId: props.caseId,
                employeeFirstName: props.employeeFirstName,
                employeeLastName: props.employeeLastName,
                secondaryStatus: props.secondaryStatus,
            }));
        }
    }, [dispatch, props.caseId, props.caseType, props.employeeFirstName, props.employeeLastName,
        props.include, props.path, props.secondaryStatus, props.status]);

    React.useEffect(() => {
        if (props.allowedProducts?.length > 0) {
            const allowedProductOptions = PRODUCT_TYPES
                .filter(p => props.allowedProducts.some(a => a === p.value))
                .map(status => ({ key: status.value, value: status.value, text: status.displayName }));

            setProductOptions(allowedProductOptions);
            setProductOptionsLoaded(true);
        }
    }, [props.allowedProducts]);

    const onFilterCaseIdChange = React.useCallback((_event: any, { value }: InputOnChangeData) => {
        setCaseId(value);
        debounceUpdateCaseIdFilter(value);
    }, [debounceUpdateCaseIdFilter]);

    const onFilterFirstNameChange = React.useCallback((_event: any, { value }: InputOnChangeData) => {
        setEmployeeFirstName(value);
        debounceUpdateFirstNameFilter(value);
    }, [debounceUpdateFirstNameFilter]);

    const onFilterLastNameChange = React.useCallback((_event: any, { value }: InputOnChangeData) => {
        setEmployeeLastName(value);
        debounceUpdateLastNameFilter(value);
    }, [debounceUpdateLastNameFilter]);

    const onFilterCaseTypeChange = React.useCallback((value: string[]) => {
        let values = "";
        if (value?.length) {
            values = value.join(filterSeparator);
        }

        if (values !== caseType) {
            setCaseType(values);
            debounceUpdateCaseTypeFilter(values);
        }
    }, [caseType, debounceUpdateCaseTypeFilter]);

    const onStatusValueChange = React.useCallback((value: string[]) => {
        const values = value?.join(filterSeparator);

        if (values !== props.status) {
            props.onStatusValueChange(values);
        }
    }, [props]);

    const onSecondaryStatusValueChange = React.useCallback((value: string) => {
        if (value !== props.secondaryStatus) {
            props.onSecondaryStatusValueChange(value);
        }
    }, [props]);

    const getSecondaryStatus = (ohCase: OhCaseListModel): SecondaryCaseStatus | undefined => {
        return ohCase.secondaryCaseStatus !== SecondaryCaseStatus.Unknown &&
            ohCase.secondaryCaseStatus !== SecondaryCaseStatus.Pending &&
            ohCase.secondaryCaseStatus;
    };

    const isAllView = (): boolean => !props.include || props.include === "All";

    const caseStatusFilter = (
        <Grid.Column width={8} textAlign="right">
            <div className="state-filter">
                <Input.DropdownMulti
                    value={props.status ? props.status.split(filterSeparator) : []}
                    options={stateOptions}
                    onChange={onStatusValueChange}
                    placeholder="Status"
                />
            </div>
        </Grid.Column>
    );

    const caseStatusAndSecondaryStatusFilters = (
        <>
            <Grid.Column width={5} textAlign="right">
                <div className="state-filter">
                    <Input.DropdownMulti
                        value={props.status ? props.status.split(filterSeparator) : []}
                        options={stateOptions}
                        onChange={onStatusValueChange}
                        placeholder="Status"
                    />
                </div>
            </Grid.Column>
            <Grid.Column width={3} textAlign="right">
                <div className="state-filter">
                    <Input.Dropdown
                        value={props.secondaryStatus}
                        options={secondaryCaseStatusOptions}
                        onChange={onSecondaryStatusValueChange}
                    />
                </div>
            </Grid.Column>
        </>
    );

    if (areCustomMedicalTypesLoaded === false)
    {
        return <Loader active />;
    }

    return (
        <Grid container className="nomargintop">
            <Grid.Row verticalAlign="middle" columns="equal">
                <Grid.Column>
                    <h1>Cases</h1>
                </Grid.Column>
                <Grid.Column>
                    {isAllView() ? caseStatusFilter : caseStatusAndSecondaryStatusFilters}
                </Grid.Column>
            </Grid.Row>
            <Divider />
            <Grid.Row>
                <Grid.Column width={16}>
                    <Table>
                        <Table.Header>
                            <Table.Row>
                                <Table.HeaderCell>Case&nbsp;ID</Table.HeaderCell>
                                {!isAllView() && <Table.HeaderCell>{props.isDefaultCustomerType && <>Employee </>}Unique&nbsp;ID</Table.HeaderCell>}
                                <Table.HeaderCell>{props.isDefaultCustomerType && <>Employee </>}First&nbsp;Name</Table.HeaderCell>
                                <Table.HeaderCell>{props.isDefaultCustomerType && <>Employee </>}Last&nbsp;Name</Table.HeaderCell>
                                <Table.HeaderCell>Last Updated</Table.HeaderCell>
                                {isAllView() && <Table.HeaderCell width={3}>Case Type</Table.HeaderCell>}
                                {isAllView() && <Table.HeaderCell>Recall Date</Table.HeaderCell>}
                                {!isAllView() && <Table.HeaderCell>Org level&nbsp;1</Table.HeaderCell>}
                                <Table.HeaderCell>Case Status</Table.HeaderCell>
                                {!isAllView() && <Table.HeaderCell>Secondary Status</Table.HeaderCell>}
                                {isAllView() && <Table.HeaderCell width={4}>Case Information</Table.HeaderCell>}
                            </Table.Row>
                            <Table.Row>
                                <Table.Cell>
                                    <SemanticInput
                                        value={caseId}
                                        onChange={onFilterCaseIdChange}
                                        icon="search"
                                        placeholder={isAllView() ? "Sear..." : "Search..."}
                                        size="mini"
                                        type="number"
                                        className={isAllView() ? "case-id-input" : undefined}
                                    />
                                </Table.Cell>
                                {!isAllView() && <Table.Cell />}
                                <Table.Cell>
                                    <SemanticInput
                                        value={employeeFirstName}
                                        onChange={onFilterFirstNameChange}
                                        icon="search"
                                        placeholder="Search..."
                                        size="mini"
                                        className={isAllView() ? "employee-fn-input" : undefined}
                                    />
                                </Table.Cell>
                                <Table.Cell>
                                    <SemanticInput
                                        value={employeeLastName}
                                        onChange={onFilterLastNameChange}
                                        icon="search"
                                        placeholder="Search..."
                                        size="mini"
                                        className={isAllView() ? "employee-ln-input" : undefined}
                                    />
                                </Table.Cell>
                                <Table.Cell />
                                <Table.Cell className="case-type-cell">
                                    {isAllView() && productOptionsLoaded ?
                                        (<Input.DropdownMulti
                                            value={caseType ? caseType.split(filterSeparator) : []}
                                            options={productOptions}
                                            onChange={onFilterCaseTypeChange}
                                            placeholder="Search..."
                                        />) :
                                        <></>
                                    }
                                </Table.Cell>
                                <Table.Cell />
                                {!isAllView() && <Table.Cell />}
                                {isAllView() && <Table.Cell />}
                                {isAllView() && <Table.Cell />}
                            </Table.Row>
                        </Table.Header>
                        <Table.Body>
                            {props.ohCases && ohCasesWithFilters()
                                .map(ohCase => (
                                    <Table.Row key={ohCase.id}>
                                        <Table.Cell>
                                            <Link href={`${props.path}/${ohCase.id}`}>
                                                {ohCase.caseId}
                                            </Link>
                                        </Table.Cell>
                                        {!isAllView() && <Table.Cell>
                                            {ohCase.employeeSecondaryReference}
                                        </Table.Cell>}
                                        <Table.Cell>
                                            <Link href={`${props.path}/${ohCase.id}`}>
                                                {ohCase.employeeFirstName}
                                            </Link>
                                        </Table.Cell>
                                        <Table.Cell>
                                            <Link href={`${props.path}/${ohCase.id}`}>
                                                {ohCase.employeeLastName}
                                            </Link>
                                        </Table.Cell>
                                        <Table.Cell>
                                            { ohCase?.lastUpdated && ohCase?.lastUpdated?.format("DD/MM/YYYY")}
                                        </Table.Cell>
                                        {isAllView() && <Table.Cell>
                                            {ohCase.caseType}
                                        </Table.Cell>}
                                        {isAllView() && <Table.Cell>
                                            { ohCase?.localDueDate && ohCase?.localDueDate?.format("DD/MM/YYYY")}
                                        </Table.Cell>}
                                        {!isAllView() && <Table.Cell>
                                            {ohCase.orgStructureOneName}
                                        </Table.Cell>}
                                        <Table.Cell>
                                            {ohCase.caseStatus}
                                        </Table.Cell>
                                        {!isAllView() && <Table.Cell>
                                            {getSecondaryStatus(ohCase)}
                                        </Table.Cell>}
                                        {isAllView() && <Table.Cell>
                                            {getCaseInformation(ohCase, customMedicalTypes)}
                                        </Table.Cell>}
                                    </Table.Row>
                                ))}
                        </Table.Body>
                    </Table>
                    {props.ohCases.length >= 100 && "There may be more cases. Please refine your search."}
                </Grid.Column>
            </Grid.Row>
        </Grid>
    );

    function ohCasesWithFilters() {
        const filteredCases = props.ohCases.filter(ohCase => (!props.status || props.status.split(filterSeparator).some(c => c === ohCase.caseStatus)) &&
            ohCase.employeeFirstName?.toUpperCase()?.startsWith(employeeFirstName?.toUpperCase()) &&
            ohCase.employeeLastName?.toUpperCase()?.startsWith(employeeLastName?.toUpperCase()) &&
            (!caseId || ohCase.caseId === caseId));

        return filteredCases;
    }
};

function mapStateToProps(state: SelectorState & ApplicationState): StateProps {
    return {
        path: state.router.pathname!,
        ohCases: ohCasesSelector(state),
        status: state.router.query?.status || "",
        secondaryStatus: state.router.query?.secondaryStatus || "All",
        caseId: state.router.query?.caseId || "",
        employeeFirstName: state.router.query?.employeeFirstName || "",
        employeeLastName: state.router.query?.employeeLastName || "",
        isDefaultCustomerType: state.customerDetails?.isDefaultCustomerType,
        caseType: state.router.query?.caseType || "",
        allowedProducts: state.allowedProducts
    };
}

function mapDispatchToProps(dispatch: any): DispatchProps {
    return {
        onFilterCaseIdChange: caseId => dispatch(replace({ query: { caseId } }, { persistQuery: true })),
        onFilterFirstNameChange: firstName => dispatch(replace({ query: { employeeFirstName: firstName } }, { persistQuery: true })),
        onFilterLastNameChange: lastName => dispatch(replace({ query: { employeeLastName: lastName } }, { persistQuery: true })),
        onStatusValueChange: status => dispatch(replace({ query: { status } }, { persistQuery: true })),
        onSecondaryStatusValueChange: secondaryStatus => dispatch(replace({ query: { secondaryStatus } }, { persistQuery: true })),
        onFilterCaseTypeChange: type => dispatch(replace({ query: { caseType: type } }, { persistQuery: true }))
    };
}

export const All = connect(mapStateToProps, mapDispatchToProps)(AllUnconnected);
